import { create } from 'zustand'

export type ToastVariant = 'success' | 'info' | 'error' | 'warning' | 'generic' | 'backToExit'

export interface ToastAction {
  label: string
  onClick: () => void
  /** 'primary' = botão preenchido (mint-strong, texto branco). Padrão: link/texto. */
  style?: 'primary' | 'text'
}

export interface Toast {
  id: string
  message: string
  variant: ToastVariant
  durationMs: number
  action?: ToastAction
  /** Título para notificações push (layout estruturado). */
  title?: string
  /** Exceção: toast inteiro clicável; tap em qualquer lugar executa action.onClick. Mostra ícone refresh, sem botão X. */
  tapToAction?: boolean
  createdAt: number
}

const DEFAULT_DURATION_MS = 2000
const MAX_TOASTS = 5

interface ToastStoreState {
  toasts: Toast[]
  add: (message: string, options?: {
    variant?: ToastVariant
    durationMs?: number
    action?: ToastAction
    title?: string
    /** Exceção: toast inteiro clicável; tap executa action.onClick. Ícone refresh, sem X. */
    tapToAction?: boolean
  }) => string
  remove: (id: string) => void
}

let idCounter = 0
const generateId = () => `toast-${Date.now()}-${++idCounter}`

const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>()

export const useToastStore = create<ToastStoreState>((set, get) => ({
  toasts: [],

  add: (message, options = {}) => {
    const id = generateId()
    const {
      variant = 'success',
      durationMs = DEFAULT_DURATION_MS,
      action,
      title,
      tapToAction,
    } = options

    const toast: Toast = {
      id,
      message,
      variant,
      durationMs,
      action,
      title,
      tapToAction,
      createdAt: Date.now(),
    }

    set((state) => {
      const next = [toast, ...state.toasts].slice(0, MAX_TOASTS)
      return { toasts: next }
    })

    if (durationMs > 0) {
      const timerId = setTimeout(() => {
        dismissTimers.delete(id)
        get().remove(id)
      }, durationMs)
      dismissTimers.set(id, timerId)
    }

    return id
  },

  remove: (id) => {
    const timerId = dismissTimers.get(id)
    if (timerId !== undefined) {
      clearTimeout(timerId)
      dismissTimers.delete(id)
    }
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },
}))
